import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { t } from '../utils/translations';
import { Menu, X, Sun, Moon, Globe } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { language, toggleLanguage } = useLanguage();
  const { theme, toggleTheme, getThemeClasses } = useTheme();
  const location = useLocation();
  
  const themeClasses = getThemeClasses();

  const navItems = [
    { key: 'home', path: '/' },
    { key: 'about', path: '/about' },
    { key: 'ministries', path: '/ministries' },
    { key: 'sermons', path: '/sermons' },
    { key: 'livestream', path: '/livestream' },
    { key: 'resources', path: '/resources' },
    { key: 'gallery', path: '/gallery' },
    { key: 'donate', path: '/donate' },
    { key: 'contact', path: '/contact' }
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <nav className="bg-white dark:bg-gray-900 shadow-md fixed w-full top-0 z-50 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_188dffff-cf47-49bb-8808-5c961ec9946c/artifacts/exaakh62_channels4_profile.jpg" 
              alt="Church Logo" 
              className="h-14 w-14 rounded-full object-cover"
            />
            <div className="flex flex-col">
              <span className={`text-lg font-bold ${themeClasses.primaryText} leading-tight`}>
                {t(language, 'home.churchName')}
              </span>
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {language === 'en' ? 'Orthodox Tewahdo' : 'ኦርቶዶክስ ተዋህዶ'}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.path}
                className={`px-4 py-2 rounded-lg transition-all duration-200 font-medium ${
                  isActive(item.path)
                    ? themeClasses.primary + ' text-white shadow-md'
                    : 'text-gray-700 dark:text-gray-300 ' + themeClasses.lightBg.replace('bg-', 'hover:bg-') + ' dark:hover:bg-gray-800'
                }`}
                data-testid={`nav-${item.key}`}
              >
                {t(language, `nav.${item.key}`)}
              </Link>
            ))}
          </div>

          {/* Theme & Language Toggle */}
          <div className="hidden md:flex items-center space-x-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle theme"
              data-testid="theme-toggle-btn"
            >
              {theme === 'light' ? (
                <Moon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              ) : (
                <Sun className="w-5 h-5 text-gray-300" />
              )}
            </button>
            <button
              onClick={toggleLanguage}
              className="px-3 py-2 rounded-lg bg-amber-600 text-white font-medium hover:bg-amber-700 transition-colors flex items-center space-x-1"
              data-testid="language-toggle-btn"
            >
              <Globe className="w-4 h-4" />
              <span>{language === 'en' ? 'Ti' : 'En'}</span>
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? (
              <X className="w-6 h-6 text-gray-700 dark:text-gray-300" />
            ) : (
              <Menu className="w-6 h-6 text-gray-700 dark:text-gray-300" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
          <div className="px-4 pt-2 pb-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-3 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'bg-amber-600 text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
                data-testid={`mobile-nav-${item.key}`}
              >
                {t(language, `nav.${item.key}`)}
              </Link>
            ))}
            <div className="flex items-center space-x-2 pt-2">
              <button
                onClick={toggleTheme}
                className="flex-1 px-4 py-3 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center justify-center space-x-2"
              >
                {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                <span>{theme === 'light' ? 'Dark' : 'Light'}</span>
              </button>
              <button
                onClick={toggleLanguage}
                className="flex-1 px-4 py-3 rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition-colors flex items-center justify-center space-x-2"
              >
                <Globe className="w-5 h-5" />
                <span>{language === 'en' ? 'ትግርኛ' : 'English'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;